import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  FolderTree, Plus, Pencil, Trash2, Hash, Layers, Search, X,
  ChevronRight, ChevronDown, Building2,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, EmptyState, AppButton, ConfirmModal,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./QuizCategoriesV2.scss";

const emptyForm = { name: "", description: "", icon: "", tags: [], parentCategory: null };

/**
 * QuizCategoriesPage — modernised UX.
 *
 * Highlights:
 *   • Cleaner card with hover-revealed actions, expandable subcategories.
 *   • AppButton for all actions.
 *   • Portal-based modal (no Bootstrap dependency) with proper a11y.
 *   • Cross-branch chip in header (matches Quiz Center).
 *   • Light + dark mode via CSS variables.
 */
const QuizCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/quiz-categories", {
        headers: { "X-Branch-Id": "all" },
      });
      setCategories(data.categories || []);
    } catch {
      setToast({ show: true, message: "Failed to load categories", type: "error" });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openAdd = (parentId = null) => {
    setEditingCategory(null);
    setForm({ ...emptyForm, parentCategory: parentId });
    setTagInput("");
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditingCategory(cat);
    setForm({
      name: cat.name,
      description: cat.description || "",
      icon: cat.icon || "",
      tags: cat.tags || [],
      parentCategory: cat.parentCategory || null,
    });
    setTagInput("");
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingCategory) {
        await apiClient.put(`/quiz-categories/${editingCategory._id}`, form);
        setToast({ show: true, message: "Category updated", type: "success" });
      } else {
        await apiClient.post("/quiz-categories", form);
        setToast({ show: true, message: "Category created", type: "success" });
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to save category",
        type: "error",
      });
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/quiz-categories/${deleteTarget._id}`);
      setToast({ show: true, message: "Category deleted", type: "success" });
      fetchCategories();
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to delete category",
        type: "error",
      });
    }
    setDeleteTarget(null);
  };

  // ── Tag input helpers ───────────────────────────────────────────────
  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !form.tags.includes(trimmed)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setTagInput("");
  };
  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };
  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === "Backspace" && !tagInput && form.tags.length > 0) {
      removeTag(form.tags[form.tags.length - 1]);
    }
  };

  // ── Filtering ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search) return categories;
    const q = search.toLowerCase();
    return categories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(q) ||
        (cat.description || "").toLowerCase().includes(q) ||
        (cat.tags || []).some((t) => t.toLowerCase().includes(q)) ||
        (cat.subcategories || []).some((s) => s.name.toLowerCase().includes(q))
    );
  }, [categories, search]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totalCategories = categories.length;
  const totalSubcategories = categories.reduce(
    (sum, c) => sum + (c.subcategories?.length || 0), 0
  );
  const totalQuestions = categories.reduce(
    (sum, c) => sum + (c.questionCount || 0), 0
  );

  const modalTitle = editingCategory
    ? `Edit ${editingCategory.parentCategory ? "Subcategory" : "Category"}`
    : form.parentCategory ? "Add Subcategory" : "Add Category";

  return (
    <div className="quiz-categories-v2">
      <PageHeader
        icon={FolderTree}
        title="Quiz Categories"
        description="Organise your question bank by topic, subject or skill."
        actions={
          <div className="qcat-header-actions">
            <span className="qcat-chip" title="Categories are shared across every branch">
              <Building2 size={13} /> All branches
            </span>
            <AppButton variant="default" onClick={() => openAdd()}>
              <Plus size={16} /> Add Category
            </AppButton>
          </div>
        }
      />

      {/* ── Compact stats + search row ─────────────────────────────── */}
      <div className="qcat-toolbar">
        <div className="qcat-stats">
          <div className="qcat-stat">
            <span className="qcat-stat-value">{loading ? "—" : totalCategories}</span>
            <span className="qcat-stat-label">Categories</span>
          </div>
          <div className="qcat-stat">
            <span className="qcat-stat-value">{loading ? "—" : totalSubcategories}</span>
            <span className="qcat-stat-label">Subcategories</span>
          </div>
          <div className="qcat-stat">
            <span className="qcat-stat-value">{loading ? "—" : totalQuestions}</span>
            <span className="qcat-stat-label">Questions</span>
          </div>
        </div>

        <div className="qcat-search">
          <Search size={16} className="qcat-search-icon" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories, subcategories or tags..."
          />
          {search && (
            <button
              type="button"
              className="qcat-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── List / Empty state ──────────────────────────────────────── */}
      {loading ? (
        <div className="qcat-loading">
          <div className="qcat-skeleton" />
          <div className="qcat-skeleton" />
          <div className="qcat-skeleton" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<FolderTree size={40} />}
          title={search ? "No matches" : "No categories yet"}
          description={
            search
              ? "Try a different search term."
              : "Create your first category to start organising questions."
          }
          action={
            !search && (
              <AppButton variant="default" onClick={() => openAdd()}>
                <Plus size={16} /> Create Category
              </AppButton>
            )
          }
        />
      ) : (
        <div className="qcat-grid">
          {filtered.map((cat) => {
            const isOpen = expanded.has(cat._id);
            const subs = cat.subcategories || [];
            return (
              <article key={cat._id} className="qcat-card">
                <header className="qcat-card-head">
                  <div className="qcat-card-icon" aria-hidden="true">
                    {cat.icon || <FolderTree size={18} />}
                  </div>
                  <div className="qcat-card-title-wrap">
                    <h3 className="qcat-card-title">{cat.name}</h3>
                    {cat.description && (
                      <p className="qcat-card-desc">{cat.description}</p>
                    )}
                  </div>
                  <div className="qcat-card-actions">
                    <button
                      type="button"
                      className="qcat-icon-btn"
                      onClick={() => openEdit(cat)}
                      title="Edit category"
                      aria-label="Edit category"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className="qcat-icon-btn qcat-icon-btn--danger"
                      onClick={() => setDeleteTarget(cat)}
                      title="Delete category"
                      aria-label="Delete category"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </header>

                <div className="qcat-card-meta">
                  <span className="qcat-meta-pill">
                    <Hash size={12} /> {cat.questionCount || 0} questions
                  </span>
                  <span className="qcat-meta-pill">
                    <Layers size={12} /> {subs.length} subcategor{subs.length === 1 ? "y" : "ies"}
                  </span>
                </div>

                {cat.tags && cat.tags.length > 0 && (
                  <div className="qcat-card-tags">
                    {cat.tags.map((tag) => (
                      <span className="qcat-tag" key={tag}>{tag}</span>
                    ))}
                  </div>
                )}

                <footer className="qcat-card-foot">
                  {subs.length > 0 ? (
                    <button
                      type="button"
                      className="qcat-toggle"
                      onClick={() => toggleExpand(cat._id)}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      {isOpen ? "Hide" : "Show"} subcategories ({subs.length})
                    </button>
                  ) : <span />}
                  <button
                    type="button"
                    className="qcat-add-sub"
                    onClick={() => openAdd(cat._id)}
                  >
                    <Plus size={14} /> Subcategory
                  </button>
                </footer>

                {isOpen && subs.length > 0 && (
                  <ul className="qcat-sublist">
                    {subs.map((sub) => (
                      <li key={sub._id} className="qcat-subitem">
                        <span className="qcat-subitem-name">
                          <ChevronRight size={12} /> {sub.name}
                        </span>
                        <span className="qcat-subitem-count">
                          {sub.questionCount || 0} questions
                        </span>
                        <span className="qcat-subitem-actions">
                          <button
                            type="button"
                            className="qcat-icon-btn qcat-icon-btn--sm"
                            onClick={() => openEdit(sub)}
                            aria-label="Edit subcategory"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            className="qcat-icon-btn qcat-icon-btn--sm qcat-icon-btn--danger"
                            onClick={() => setDeleteTarget(sub)}
                            aria-label="Delete subcategory"
                          >
                            <Trash2 size={12} />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* ── Add / Edit Modal (portal, design-system aware) ───────────── */}
      {showModal && createPortal(
        <div className="qcat-modal-backdrop" onClick={() => setShowModal(false)}>
          <div
            className="qcat-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qcat-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="qcat-modal-head">
              <h2 id="qcat-modal-title" className="qcat-modal-title">{modalTitle}</h2>
              <button
                type="button"
                className="qcat-modal-close"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <form onSubmit={handleSave}>
              <div className="qcat-modal-body">
                <div className="qcat-field">
                  <label htmlFor="cat-name" className="qcat-label">
                    Name <span className="qcat-required">*</span>
                  </label>
                  <input
                    id="cat-name"
                    type="text"
                    className="qcat-input"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Aptitude, GRE Quant, IELTS Reading"
                    required
                    autoFocus
                  />
                </div>

                <div className="qcat-field">
                  <label htmlFor="cat-desc" className="qcat-label">Description</label>
                  <textarea
                    id="cat-desc"
                    className="qcat-input qcat-textarea"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description (optional)"
                  />
                </div>

                <div className="qcat-field-row">
                  <div className="qcat-field">
                    <label htmlFor="cat-icon" className="qcat-label">Icon</label>
                    <input
                      id="cat-icon"
                      type="text"
                      className="qcat-input qcat-input--icon"
                      value={form.icon}
                      onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
                      placeholder="📝"
                      maxLength={4}
                    />
                    <span className="qcat-help">Single emoji shown on the card.</span>
                  </div>

                  <div className="qcat-field qcat-field--grow">
                    <label htmlFor="cat-tags" className="qcat-label">
                      Tags <span className="qcat-help-inline">— press Enter to add</span>
                    </label>
                    <div className="qcat-tags-input">
                      {form.tags.map((tag) => (
                        <span className="qcat-input-tag" key={tag}>
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            aria-label={`Remove ${tag}`}
                          >
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                      <input
                        id="cat-tags"
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        onBlur={() => tagInput && addTag(tagInput)}
                        placeholder={form.tags.length === 0 ? "Placement, IELTS, Campus Test…" : ""}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <footer className="qcat-modal-foot">
                <AppButton
                  variant="outline"
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </AppButton>
                <AppButton variant="default" type="submit" disabled={saving || !form.name.trim()}>
                  {saving ? "Saving…" : editingCategory ? "Save changes" : "Create"}
                </AppButton>
              </footer>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ── Delete confirmation ─────────────────────────────────────── */}
      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleteTarget?.parentCategory ? "subcategory" : "category"}?`}
        message={
          <>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            This action cannot be undone.
            {(deleteTarget?.questionCount || 0) > 0 && (
              <div style={{ marginTop: 8, color: "hsl(var(--destructive, 0 84% 60%))" }}>
                This category has {deleteTarget.questionCount} question(s) attached.
              </div>
            )}
          </>
        }
        confirmText="Delete"
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default QuizCategoriesPage;
